import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useForm } from "react-hook-form";
import axios from 'axios';
import { useTranslation } from "react-i18next";
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import TopBarNav from '../styled/Topbar';
import { BeatLoader } from 'react-spinners';

/* ─── animations ─── */
const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 0.8; }
`;

/* ─── layout ─── */
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 15s ease infinite;
  padding: 5rem 1rem 2rem;
  position: relative;
  overflow: hidden;
`;

const Orb = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none; z-index: 0; overflow: hidden;
  &::before, &::after {
    content: ''; position: absolute; border-radius: 50%; filter: blur(80px);
    animation: ${pulse} 6s ease-in-out infinite;
  }
  &::before { width: 400px; height: 400px; background: rgba(99,102,241,0.2); top: -8%; left: -8%; }
  &::after { width: 350px; height: 350px; background: rgba(236,72,153,0.15); bottom: -5%; right: -6%; animation-delay: 2s; }
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  backdrop-filter: blur(16px);
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const InfoText = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.55);
  text-align: center;
  line-height: 1.6;
  max-width: 320px;
`;

const SuccessText = styled.p`
  font-size: 0.95rem;
  color: #6ee7b7;
  text-align: center;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  max-width: 280px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.7rem 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  transition: border-color 0.2s, background 0.2s;

  &::placeholder { color: rgba(255, 255, 255, 0.3); }
  &:focus { outline: none; border-color: rgba(99,102,241,0.5); background: rgba(255,255,255,0.08); }
`;

const PasswordWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordToggle = styled.span`
  position: absolute;
  top: 50%; right: 10px;
  transform: translateY(-50%);
  cursor: pointer;
  color: rgba(255,255,255,0.3);
  font-size: 1.3rem;
  display: flex;
  transition: color 0.2s;
  &:hover { color: rgba(255,255,255,0.6); }
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 0.7rem 0;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 3px 14px rgba(99,102,241,0.3);

  &:hover { transform: translateY(-1px); box-shadow: 0 5px 20px rgba(99,102,241,0.45); }
  &:active { transform: translateY(0); }
`;

const ErrorText = styled.p`
  font-size: 0.78rem;
  color: #fca5a5;
  margin: 0;
  line-height: 1.5;
  text-align: center;
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(30, 27, 75, 0.85);
  display: flex; align-items: center; justify-content: center;
  z-index: 10;
  border-radius: 24px;
`;

/* ─── component ─── */
const ResetPassword = () => {
    const { register, handleSubmit, watch, formState: { errors } } = useForm();
    const [requestValid, setRequestValid] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const { t } = useTranslation();

    const passwordValue = watch("password", "");

    const validateResetRequest = async () => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const token = urlParams.get('token');
            const response = await axios.get(`${process.env.REACT_APP_SERVER}/reset-password/${token}`, { withCredentials: true });
            if (response.data.success) {
                setRequestValid(true);
            } else {
                setIsError(true);
                setErrorMessage(t('InvalidRequestText'));
            }
        } catch (error) {
            setIsError(true);
            setErrorMessage(t('InvalidRequestText'));
        }
    };

    const onSubmit = async (data) => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        setIsLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_SERVER}/reset-password`, { password: data.password, token }, { withCredentials: true });
            if (response.data.success) {
                setShowSuccess(true);
            } else {
                setErrorMessage(t('GenericErrorText'));
                setIsError(true);
                setRequestValid(false);
            }
        } catch (error) {
            setErrorMessage(t('GenericErrorText'));
            setIsError(true);
        }
        setIsLoading(false);
    };

    // Landing state — validate token
    if (!requestValid && !isError) {
        return (
            <PageWrapper>
                <Orb />
                <TopBarNav onSignInClick={() => {}} />
                <Card>
                    <InfoText>{t('RestPasswordLandingText')}</InfoText>
                    <ActionButton onClick={validateResetRequest} style={{ maxWidth: '280px' }}>{t('ContinueButton')}</ActionButton>
                </Card>
            </PageWrapper>
        );
    }

    // Error state
    if (isError) {
        return (
            <PageWrapper>
                <Orb />
                <TopBarNav onSignInClick={() => {}} />
                <Card>
                    <InfoText>{errorMessage}</InfoText>
                </Card>
            </PageWrapper>
        );
    }

    // Success state
    if (showSuccess) {
        return (
            <PageWrapper>
                <Orb />
                <TopBarNav onSignInClick={() => {}} />
                <Card>
                    <SuccessText>{t('PasswordResetSuccessText')}</SuccessText>
                </Card>
            </PageWrapper>
        );
    }

    // Reset form
    return (
        <PageWrapper>
            <Orb />
            <TopBarNav onSignInClick={() => {}} />
            <Card>
                {isLoading && <SpinnerOverlay><BeatLoader color="#a5b4fc" size={12} /></SpinnerOverlay>}

                <Form onSubmit={handleSubmit(onSubmit)}>
                    <PasswordWrapper>
                        <Input
                            type={showPassword ? "text" : "password"}
                            placeholder={t('SignupPasswordText')}
                            style={{ paddingRight: '2.5rem' }}
                            {...register("password", { required: true, pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{7,}$/ })}
                            maxLength={50}
                        />
                        <PasswordToggle onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <MdVisibilityOff size={22} /> : <MdVisibility size={22} />}
                        </PasswordToggle>
                    </PasswordWrapper>
                    {errors.password && <ErrorText>Password must be at least 7 characters with uppercase, lowercase, and a number.</ErrorText>}

                    <PasswordWrapper>
                        <Input
                            type={showPassword ? "text" : "password"}
                            placeholder={t('RetypePasswordText')}
                            style={{ paddingRight: '2.5rem' }}
                            {...register("password-second", {
                                required: true,
                                validate: value => value === passwordValue || t('PasswordsDontMatchText')
                            })}
                            maxLength={50}
                        />
                        <PasswordToggle onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <MdVisibilityOff size={22} /> : <MdVisibility size={22} />}
                        </PasswordToggle>
                    </PasswordWrapper>
                    {errors["password-second"] && (
                        errors["password-second"].type === 'validate'
                            ? <ErrorText>{errors["password-second"].message}</ErrorText>
                            : <ErrorText>Password must be at least 7 characters with uppercase, lowercase, and a number.</ErrorText>
                    )}

                    <ActionButton type="submit">{t('SaveChangesButton')}</ActionButton>
                </Form>
            </Card>
        </PageWrapper>
    );
};

export default ResetPassword;
